import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export const reactFlowSelector = (state: ReactFlowState) => ({
  activeFunctionId: state.activeFunctionId,
  activeStepId: state.activeStepId,
  openFlowFunction: state.openFlowFunction,
  closeFlowFunction: state.closeFlowFunction,
  openStepConfigurationPanel: state.openStepConfigurationPanel,
  closeStepConfigurationPanel: state.closeStepConfigurationPanel,

  // Sidebar management
  activeSidebar: state.activeSidebar,
  setActiveSidebar: state.setActiveSidebar,
  closeSidebar: state.closeSidebar,
  isSidebarOpen: state.isSidebarOpen,
});

type OnFunctionChange = (newName: string, prompt: string | undefined) => void;

type FunctionSidebarPayload = {
  type: 'function';
  stepId?: string;
  onChange?: OnFunctionChange;
};

type ConditionSidebarPayload = {
  type: 'condition';
};

type EntitySidebarPayload = {
  type: 'entity';
};

type StepConfigurationSidebarPayload = {
  type: 'step_configuration';
};

type FunctionStepSidebarPayload = {
  type: 'function_step';
  id?: string;
};

type SidebarPayload =
  | FunctionSidebarPayload
  | ConditionSidebarPayload
  | EntitySidebarPayload
  | StepConfigurationSidebarPayload
  | FunctionStepSidebarPayload;

export type SidebarType = SidebarPayload['type'] | null;
interface ReactFlowState {
  activeFunctionId?: string;
  activeStepId?: string;
  onFunctionChange?: OnFunctionChange;
  openFlowFunction: (
    id: string,
    currentStepId?: string,
    onChange?: OnFunctionChange,
  ) => void;
  closeFlowFunction: () => void;
  openStepConfigurationPanel: (stepName: string, callback?: () => void) => void;
  closeStepConfigurationPanel: (callback?: () => void) => void;
  currentStepId?: string;

  // Sidebar management state
  activeSidebar: SidebarPayload | undefined;
  setActiveSidebar: (sidebar: SidebarPayload | undefined) => void;
  closeSidebar: () => void;
  isSidebarOpen: (sidebar: SidebarType) => boolean;
}

const useFlowDetailsStore = create<ReactFlowState>()(
  devtools((set, get) => {
    return {
      // Existing function state
      openFlowFunction: (activeFunctionId, currentStepId, onChange) => {
        set({
          activeFunctionId,
          onFunctionChange: onChange,
          activeStepId: undefined,
          currentStepId,
          // Close other sidebars when opening function
          activeSidebar: {
            type: 'function',
          },
        });
      },
      closeFlowFunction: () => {
        set({
          activeFunctionId: undefined,
          onFunctionChange: undefined,
          activeSidebar: undefined,
        });
      },
      openStepConfigurationPanel: (stepName, callback) => {
        set({
          activeStepId: stepName,
          activeFunctionId: undefined,
          activeSidebar: {
            type: 'step_configuration',
          },
        });
        callback?.();
      },
      closeStepConfigurationPanel: callback => {
        set({
          activeStepId: undefined,
          activeSidebar: undefined,
        });
        callback?.();
      },
      // Sidebar management state
      activeSidebar: undefined,
      activeFunctionStepId: undefined,
      setActiveSidebar: payload => {
        set({
          activeSidebar: payload,
        });
      },
      closeSidebar: () => {
        set({
          activeSidebar: undefined,
        });
      },
      isSidebarOpen: sidebar => {
        return get().activeSidebar?.type === sidebar;
      },
    };
  }),
);

export default useFlowDetailsStore;
